import _ from "lodash";

export const fallbackImageUrl = "https://static.koboscm.com/images/RakutenKobo/devices/image-not-found.png";

export const getMainSubOrder = order => {
    if (!order || !order.sub_orders) {
        return null;
    }

    return (
        order.sub_orders.find(
            subOrder => !subOrder.order_statuses.some(status => (status.status === "CANCELLED" || status.status === "REFUNDED") && status.date)
        ) || null
    );
};

export const subOrderIsCancelled = sub_order => {
    return sub_order && sub_order.order_statuses && sub_order.order_statuses.some(os => os.status === "CANCELLED" && os.date) ? true : false;
};

export const orderIsCancelled = order => {
    return order.cancelled_at != null;
};

export const orderIsFullyRefunded = order => {
    return order.fully_refunded === true;
};

export const subOrderIsShipped = sub_order => {
    return sub_order && sub_order.order_statuses && sub_order.order_statuses.some(os => os.status === "SHIPPED" && os.date) ? true : false;
};

export const subOrderIsRefunded = sub_order => {
    return sub_order && sub_order.order_statuses && sub_order.order_statuses.some(os => os.status === "REFUNDED" && os.date) ? true : false;
};

export const subOrderIsCompleted = sub_order => {
    return !sub_order.order_statuses.some(os => os.show === true && os.date === null);
};

export const getCompleteOrderStatus = order => {
    if (order.sub_orders.every(so => so.order_statuses.some(os => os.status === "REFUNDED" && os.date && os.show))) {
        return "Refunded";
    } else if (order.sub_orders.every(so => so.order_statuses.some(os => os.status === "DELIVERED" && os.date && os.show))) {
        return "Delivered";
    }
    return null;
};

export const isCurrentStatus = (order_status, order_statuses) => {
    if (!order_status.show) return false;
    const visibleWithoutDate = order_statuses.filter(os => !os.date && os.show);
    return visibleWithoutDate.length > 0 && visibleWithoutDate[0].status === order_status.status;
};

export const isNextStatus = (order_status, order_statuses) => {
    const visibleStatuses = order_statuses.filter(os => os.show);
    const index = visibleStatuses.indexOf(order_status);
    return index > 1 && isCurrentStatus(order_statuses[index - 1], order_statuses);
};

export const isLastStatus = (order_status, order_statuses) => {
    const visibleStatuses = order_statuses.filter(os => os.show);
    return visibleStatuses.indexOf(order_status) === visibleStatuses.length - 1;
};

export const isSecondLastStatus = (order_status, order_statuses) => {
    const visibleStatuses = order_statuses.filter(os => os.show);
    return visibleStatuses.indexOf(order_status) === visibleStatuses.length - 2;
};

export const isPreviousStatus = (order_status, order_statuses) => {
    const visibleStatuses = order_statuses.filter(os => os.show);
    const index = visibleStatuses.indexOf(order_status);
    return (
        isCurrentStatus(order_statuses[index + 2], order_statuses) || (order_statuses.every(os => os.show && os.date) && index === visibleStatuses.length - 2)
    );
};

export const isClosedStatus = (order_status, order_statuses) => {
    const visibleStatuses = order_statuses.filter(os => os.show);
    const currentStatus = order_statuses.find(os => isCurrentStatus(os, order_statuses));

    if (currentStatus) {
        const current_index = visibleStatuses.indexOf(currentStatus);
        const order_status_index = visibleStatuses.indexOf(order_status);

        if ((!isSecondLastStatus(order_status, order_statuses) && order_status_index < current_index) || order_status_index > current_index + 1) {
            return true;
        }
    } else {
        const order_status_index = visibleStatuses.indexOf(order_status);
        if (order_status_index < visibleStatuses.length - 2) {
            return true;
        }
    }

    return false;
};

export const getCurrentSubOrderStatus = sub_order => {
    return sub_order.order_statuses.find(order_status => isCurrentStatus(order_status, sub_order.order_statuses));
};

export const getSubOrderItems = (sub_order, order) => {
    const sub_order_line_items = [];

    if (order.refunded_items) {
        sub_order.line_items.forEach(line_item => {
            const refunded_item = order.refunded_items.find(ri => ri.id === line_item.id);

            if (refunded_item) {
                if (line_item.quantity > 1 && line_item.quantity > refunded_item.quantity) {
                    const temp_line_item = { ...line_item };
                    temp_line_item.quantity = line_item.quantity - refunded_item.quantity;
                    sub_order_line_items.push(temp_line_item);
                }
            } else {
                sub_order_line_items.push(line_item);
            }
        });
        return sub_order_line_items;
    }

    return sub_order.line_items;
};

export const getDeviceProtectionItems = order => {
    if (!order || !order.line_items) return [];
    return _.orderBy(
        order.line_items.filter(li => isDeviceProtectionSku(li.sku)),
        ["price"],
        ["desc"]
    );
};

export const getShippingProtectionItems = order => {
    if (!order || !order.line_items) return [];
    return _.orderBy(
        order.line_items.filter(li => isShippingProtectionSku(li.sku)),
        ["price"],
        ["desc"]
    );
};

export const orderHasShippingProtection = order => {
    return order && order.line_items && order.line_items.some(li => isShippingProtectionSku(li.sku)) ? true : false;
};

export const getItemsThatRequireShipping = order => {
    if (!order || !order.line_items) return [];
    return _.orderBy(
        order.line_items.filter(li => li.requires_shipping),
        ["price"],
        ["desc"]
    );
};

export const getCalculatedDeviceProtectionItems = order => {
    const deviceProtectionLineItems = [];

    if (order && order.line_items) {
        if (order.refunded_items) {
            order.line_items
                .filter(li => isDeviceProtectionSku(li.sku))
                .forEach(line_item => {
                    const refunded_item = order.refunded_items.find(ri => ri.id === line_item.id);

                    if (refunded_item) {
                        if (line_item.quantity > 1 && line_item.quantity > refunded_item.quantity) {
                            const temp_line_item = { ...line_item };
                            temp_line_item.quantity = line_item.quantity - refunded_item.quantity;
                            deviceProtectionLineItems.push(temp_line_item);
                        }
                    } else {
                        deviceProtectionLineItems.push(line_item);
                    }
                });
        } else {
            return _.orderBy(
                order.line_items.filter(li => isDeviceProtectionSku(li.sku)),
                ["price"],
                ["desc"]
            );
        }
    }

    return _.orderBy(deviceProtectionLineItems, ["price"], ["desc"]);
};

export const sortItems = items => {
    let sortedItems = [];

    let itemsCopy = JSON.parse(JSON.stringify(items));

    itemsCopy.forEach(item => {
        item.price = parseFloat(item.price);
    });

    itemsCopy = _.orderBy(itemsCopy, ["price", "sku"], ["desc", "asc"]);

    itemsCopy.forEach(item => {
        if (item.requires_shipping === true) {
            sortedItems.push(item);

            let deviceProtectionItems = itemsCopy.filter(
                i => isDeviceProtectionSku(i.sku) && getDeviceProtectionReferenceVariantId(i) === item.variant_id.toString()
            );

            if (deviceProtectionItems) {
                sortedItems = sortedItems.concat(deviceProtectionItems);
            }
        }
    });

    const deviceProtectionItemsNotAllocated = itemsCopy.filter(i => isDeviceProtectionSku(i.sku) && !sortedItems.some(sortedItem => sortedItem.id === i.id));
    sortedItems = sortedItems.concat(deviceProtectionItemsNotAllocated);

    const shippingProtectionItems = itemsCopy.filter(i => isShippingProtectionSku(i.sku));
    sortedItems = sortedItems.concat(shippingProtectionItems);

    return sortedItems;
};

export const sortModifiedItems = (items, order) => {
    let normalSortedList = sortItems(order.line_items);
    let sortedItems = [];
    let itemsCopy = JSON.parse(JSON.stringify(items));
    itemsCopy.forEach(item => {
        item.price = parseFloat(item.price);
    });

    normalSortedList.forEach(normalSorteditem => {
        const item = items.find(i => i.id === normalSorteditem.id);
        if (item) {
            sortedItems.push(item);
        }
    });

    return sortedItems;
};

export const getRefundedItems = order => {
    return order.refunded_items;
};

export const getSubOrders = order => {
    return order && order.sub_orders;
};

export const getVisibleSubOrders = order => {
    const sub_orders = getSubOrders(order);

    if (sub_orders) {
        return sub_orders.filter(sub_order => getSubOrderItems(sub_order, order).length > 0);
    }

    return sub_orders;
};

// Utility functions for item images
export function getItemPicture(sku) {
    if (isDeviceProtectionSku(sku)) {
        if (sku.includes("HBCAM")) {
            return `https://static.koboscm.com/images/RakutenKobo/devices/XCoverProtection.png`;
        }
        // Default to Extend protection for all other device protection SKUs
        return `https://static.koboscm.com/images/RakutenKobo/devices/ExtendProtection.png`;
    }

    if (isShippingProtectionSku(sku)) {
        return `https://static.koboscm.com/images/RakutenKobo/devices/ExtendShippingProtection.png`;
    }

    return `https://static.koboscm.com/images/RakutenKobo/devices/${sku}.png`;
}

export function getFallbackItemPicture() {
    return `https://static.koboscm.com/images/RakutenKobo/devices/image-not-found.png`;
}

export function isDeviceProtectionSku(sku) {
    return sku && (sku.toUpperCase().includes("HBCAM") || sku.toUpperCase().includes("EXTEND-PROTECTION"));
}

export function getDeviceProtectionReferenceVariantId(lineItem) {
    if (!lineItem || !lineItem.properties) {
        return null;
    }

    const referenceProperty = lineItem.properties.find(
        property => (property.name === "Reference" || property.name === "_Extend.ProductId") && property.value !== null
    );

    return referenceProperty ? referenceProperty.value.toString() : null;
}

export function isClydeXCoverProtectionLineItem(lineItem) {
    if (!lineItem || !isDeviceProtectionSku(lineItem.sku)) {
        return false;
    }

    const text = `${lineItem.title || ""} ${lineItem.name || ""}`.toUpperCase();
    return text.includes("CLYDE") || text.includes("XCOVER") || (lineItem.sku && lineItem.sku.toUpperCase().includes("HBCAM"));
}

export function isExtendProtectionLineItem(lineItem) {
    if (!lineItem || !isDeviceProtectionSku(lineItem.sku)) {
        return false;
    }

    const text = `${lineItem.title || ""} ${lineItem.name || ""}`.toUpperCase();
    return text.includes("EXTEND");
}

export function isShippingProtectionSku(sku) {
    return sku && sku.toUpperCase().includes("EXTEND-SHIPPING-PROTECTION");
}

export function isEgiftcardSku(sku) {
    return sku && sku.toUpperCase().includes("EGIFTCARD");
}

export function isPointOfSaleOrder(order) {
    return order && order.is_point_of_sale_order === true;
}

// Helper functions for formatting line item names and variants
export const getFilterDeviceProtectionLineItemName = (protectionLineItem, order) => {
    if (isClydeXCoverProtectionLineItem(protectionLineItem)) {
        const baseNameSource = protectionLineItem.name || protectionLineItem.title || "";
        const dashIndex = baseNameSource.indexOf("-");
        const firstPart = dashIndex > 0 ? baseNameSource.slice(0, dashIndex).trim() : baseNameSource;

        const referenceVariantId = getDeviceProtectionReferenceVariantId(protectionLineItem);
        let secondPart = "";

        if (referenceVariantId && order && order.line_items) {
            const referenceItem = order.line_items.find(li => li.requires_shipping === true && li.variant_id.toString() === referenceVariantId);
            secondPart = referenceItem ? referenceItem.name || referenceItem.title || "" : "";
        }

        return `${firstPart}${secondPart ? " - " + secondPart : ""}`;
    }

    const baseNameSource = protectionLineItem.title || protectionLineItem.name || "";
    const dashIndex = baseNameSource.indexOf("-");
    const firstPart = dashIndex > 0 ? baseNameSource.slice(0, dashIndex).trim() : baseNameSource;
    const productProperty = protectionLineItem.properties ? protectionLineItem.properties.find(p => p.name === "Product") : null;
    const secondPart = productProperty && productProperty.value ? productProperty.value : "";

    return `${firstPart}${secondPart ? " - " + secondPart : ""}`;
};

export const getFilterDeviceProtectionLineItemYear = protectionLineItem => {
    if (isClydeXCoverProtectionLineItem(protectionLineItem)) {
        const name = protectionLineItem.name || "";
        const slashIndex = name.indexOf("/");
        return slashIndex >= 0 ? name.substring(slashIndex + 1).trim() : "";
    }

    const termProperty = protectionLineItem.properties ? protectionLineItem.properties.find(p => p.name === "Term") : null;
    return termProperty ? termProperty.value || "" : "";
};

export const getFilterShippingProtectionLineItemName = shippingProtectionLineItem => {
    // Extract the base shipping protection plan name before the '-'
    // Example: "Extend Shipping Protection Plan - 13.00" -> "Extend Shipping Protection Plan"
    const dashIndex = shippingProtectionLineItem.name.indexOf("-");
    const baseName = dashIndex > 0 ? shippingProtectionLineItem.name.slice(0, dashIndex).trim() : shippingProtectionLineItem.name;
    return baseName;
};

export const getFilteredLineItemName = (lineItem, order) => {
    if (isDeviceProtectionSku(lineItem.sku)) {
        return getFilterDeviceProtectionLineItemName(lineItem, order);
    } else if (isShippingProtectionSku(lineItem.sku)) {
        return getFilterShippingProtectionLineItemName(lineItem);
    } else {
        return lineItem.title;
    }
};

export const getFilteredLineItemVariant = lineItem => {
    if (isDeviceProtectionSku(lineItem.sku)) {
        return getFilterDeviceProtectionLineItemYear(lineItem);
    } else if (isShippingProtectionSku(lineItem.sku)) {
        return null;
    } else {
        return lineItem.variant_title;
    }
};
